import React from 'react';
import PropTypes from 'prop-types';

/**
 * EmptyState Component
 * 
 * Display when no data or content is available.
 * Provides visual feedback and optional action button.
 * 
 * @example
 * // Basic empty state
 * <EmptyState
 *   icon={<GlobeIcon size="3xl" />}
 *   title="No websites yet"
 *   description="Create your first website to get started"
 * />
 * 
 * @example
 * // Empty state with action
 * <EmptyState
 *   icon={<SearchIcon size="3xl" />}
 *   title="No results found"
 *   description="Try adjusting your search terms"
 *   action={
 *     <button onClick={clearSearch}>Clear search</button>
 *   }
 * />
 */
const EmptyState = ({
  icon,
  title,
  description,
  action,
  className = '',
  ...props
}) => {
  return (
    <div
      className={`text-center py-12 px-4 ${className}`}
      {...props}
    >
      {/* Icon */}
      {icon && (
        <div className="flex justify-center mb-4 text-gray-400">
          {icon}
        </div>
      )}
      
      {/* Title */}
      {title && (
        <h3 className="font-heading text-lg font-bold text-gray-600 mb-2">
          {title}
        </h3>
      )}
      
      {/* Description */}
      {description && (
        <p className="text-gray-500 mb-6 max-w-md mx-auto">
          {description}
        </p>
      )}
      
      {/* Action */}
      {action && (
        <div className="flex justify-center">
          {action}
        </div>
      )}
    </div>
  );
};

EmptyState.propTypes = {
  /** Icon element to display */
  icon: PropTypes.node,
  /** Empty state title */
  title: PropTypes.string,
  /** Empty state description */
  description: PropTypes.string,
  /** Action button or element */
  action: PropTypes.node,
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default EmptyState;
